class NaptonFleetDetail extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.dialog = this.querySelector('dialog');
    this.viewport = this.dialog.querySelector('.nfd-view-stage');
    this.points = new Map();
    this.planZoom = 1;
    this.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.matches('[data-gallery-photo]')) this.selectPhoto(button);
      if (button.matches('[data-gallery-prev], [data-gallery-next]')) {
        const photos = [...this.querySelectorAll('[data-gallery-photo]')];
        const current = photos.findIndex(photo => photo.getAttribute('aria-pressed') === 'true');
        const direction = button.matches('[data-gallery-prev]') ? -1 : 1;
        this.selectPhoto(photos[(Math.max(0, current) + direction + photos.length) % photos.length]);
      }
      if (button.matches('[data-layout-choice]')) {
        const image = this.querySelector('[data-layout-image]');
        image.removeAttribute('srcset');
        image.src = button.dataset.src;
        image.dataset.original = button.dataset.original || button.dataset.src;
        image.alt = button.dataset.alt;
        image.width = Number(button.dataset.width);
        image.height = Number(button.dataset.height);
        this.querySelectorAll('[data-layout-choice]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        this.querySelector('[data-layout-status]').textContent = button.dataset.alt;
      }
      if (button.matches('[data-open-layout], [data-open-gallery]')) {
        this.opener = button;
        this.isPlan = button.matches('[data-open-layout]');
        const original = this.querySelector(this.isPlan ? '[data-layout-image]' : '[data-gallery-main]');
        const image = this.dialog.querySelector('img');
        image.src = original.dataset.original || original.currentSrc || original.src;
        image.alt = original.alt;
        image.width = original.width;
        image.height = original.height;
        this.dialog.querySelector('[data-view-caption]').textContent = original.alt;
        this.dialog.classList.remove('is-zoomed');
        this.dialog.classList.toggle('nfd-plan-dialog', this.isPlan);
        this.dialog.querySelector('[data-zoom]').hidden = this.isPlan;
        this.dialog.querySelector('[data-plan-zoom-controls]').hidden = !this.isPlan;
        this.dialog.querySelector('[data-zoom]').setAttribute('aria-pressed', 'false');
        this.setPlanZoom(1);
        this.viewport.scrollLeft = 0;
        this.viewport.scrollTop = 0;
        this.dialog.showModal();
      }
      if (button.matches('[data-close]')) this.dialog.close();
      if (button.matches('[data-zoom]')) {
        const zoomed = this.dialog.classList.toggle('is-zoomed');
        button.setAttribute('aria-pressed', String(zoomed));
      }
      if (button.matches('[data-plan-plus]')) this.setPlanZoom(this.planZoom + .5);
      if (button.matches('[data-plan-minus]')) this.setPlanZoom(this.planZoom - .5);
      if (button.matches('[data-plan-reset]')) {
        this.setPlanZoom(1);
        this.viewport.scrollLeft = 0;
        this.viewport.scrollTop = 0;
      }
      if (button.matches('[data-request-dates]')) {
        this.querySelector('[name="contact[Preferred departure]"]').value = this.querySelector('[data-trip-date]').value;
        this.querySelector('[name="contact[Holiday length]"]').value = this.querySelector('[data-trip-length]').value;
        this.querySelector('[name="contact[Guests]"]').value = this.querySelector('[data-trip-guests]').value;
        this.querySelector('[data-enquiry]').scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        this.querySelector('[name="contact[name]"]').focus({preventScroll:true});
      }
    }, {signal});
    this.viewport.addEventListener('pointerdown', event => {
      if (!this.isPlan || event.button > 0) return;
      this.points.set(event.pointerId, {x:event.clientX, y:event.clientY});
      this.viewport.setPointerCapture(event.pointerId);
      event.preventDefault();
    }, {signal});
    this.viewport.addEventListener('pointermove', event => {
      if (!this.points.has(event.pointerId)) return;
      const oldPoints = [...this.points.values()];
      const previous = this.points.get(event.pointerId);
      this.points.set(event.pointerId, {x:event.clientX,y:event.clientY});
      const newPoints = [...this.points.values()];
      if (newPoints.length === 2) {
        const distance = points => Math.hypot(points[0].x-points[1].x, points[0].y-points[1].y);
        const before = distance(oldPoints);
        if (before > 0) this.setPlanZoom(this.planZoom * distance(newPoints) / before);
      } else {
        this.viewport.scrollLeft -= event.clientX-previous.x;
        this.viewport.scrollTop -= event.clientY-previous.y;
      }
    }, {signal});
    ['pointerup','pointercancel','lostpointercapture'].forEach(type => this.viewport.addEventListener(type, event => this.points.delete(event.pointerId), {signal}));
    this.dialog.addEventListener('close', () => {this.points.clear();this.opener?.focus({preventScroll:true});}, {signal});
    this.dialog.addEventListener('click', event => {if (event.target === this.dialog) this.dialog.close();}, {signal});
    const eliteSection = this.classList.contains('nfd--elite') ? this.querySelector('.nfd-intro') : null;
    if (eliteSection && 'IntersectionObserver' in window) {
      this.eliteObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          entry.target.classList.toggle('nbv-elite-visible', entry.isIntersecting);
          if (entry.isIntersecting) entry.target.classList.add('nbv-elite-entered');
        });
      }, {threshold:.15});
      this.eliteObserver.observe(eliteSection);
    }
    const now = new Date();
    const minimum = [now.getFullYear(),String(now.getMonth()+1).padStart(2,'0'),String(now.getDate()).padStart(2,'0')].join('-');
    this.querySelectorAll('input[type=date]').forEach(input => input.min=minimum);
  }
  selectPhoto(button) {
    if (!button) return;
    const main = this.querySelector('[data-gallery-main]');
    main.src = button.dataset.src;
    main.removeAttribute('srcset');
    main.alt = button.dataset.alt;
    const photos = [...this.querySelectorAll('[data-gallery-photo]')];
    photos.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    const counter = this.querySelector('[data-gallery-count]');
    if (counter) counter.textContent = (photos.indexOf(button)+1)+' / '+photos.length;
  }
  setPlanZoom(value) {
    this.planZoom = Math.max(1, Math.min(4, value));
    this.dialog.style.setProperty('--nfp-zoom', this.planZoom);
    this.dialog.querySelector('[data-plan-zoom-output]').value = Math.round(this.planZoom*100)+'%';
    this.dialog.querySelector('[data-plan-minus]').disabled = this.planZoom <= 1;
    this.dialog.querySelector('[data-plan-plus]').disabled = this.planZoom >= 4;
  }
  disconnectedCallback() {this.eliteObserver?.disconnect();this.controller?.abort();if(this.dialog?.open)this.dialog.close();}
}
if (!customElements.get('napton-fleet-detail')) customElements.define('napton-fleet-detail',NaptonFleetDetail);
